import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from './api';
import { MIN_PASSWORD } from './passwordPolicy';
import AuthShell from './shared/AuthShell';
import { Input } from '../components/ui/input';
import PasswordInput from './shared/PasswordInput';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { RESET_PASSWORD } from '../constants/testIds';
import { Loader2, ArrowRight } from 'lucide-react';

const detailOf = (e) => e.response?.data?.detail;
const messageOf = (e, fallback = 'This link is not valid.') =>
  (typeof detailOf(e) === 'object' ? detailOf(e)?.message : detailOf(e)) || fallback;

// Ticket 140. Does NOT auto-login — the reset kills every existing
// session/JWT for the account, so the user signs in fresh (MFA still
// applies per MFA_SCOPE).
export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  // Captured once: the URL is scrubbed below, so later renders can't re-read it.
  const [token] = useState(() => params.get('token'));
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null); // dead link (404/410) → replace the form
  const [formError, setFormError] = useState(null); // validation → inline, keep the form
  const [done, setDone] = useState(false);

  // Keep the one-shot token out of history / Sentry breadcrumbs once read.
  useEffect(() => {
    if (token && window.history.replaceState) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [token]);

  if (!token) {
    return (
      <AuthShell tagline="Password reset" testId="reset-password-page">
        <h2 className="font-serif text-3xl font-semibold text-brand-navy">Link unavailable</h2>
        <p className="text-slate-500 mt-2">This link is missing its token.</p>
        <Button variant="accent" className="mt-6 justify-center" onClick={() => navigate('/portal/forgot-password')}>
          Request a new link <ArrowRight size={14} />
        </Button>
      </AuthShell>
    );
  }

  if (error) {
    return (
      <AuthShell tagline="Password reset" testId="reset-password-page">
        <h2 className="font-serif text-3xl font-semibold text-brand-navy">This link is invalid or has expired</h2>
        <p className="text-slate-500 mt-2">{error}</p>
        <Button variant="accent" className="mt-6 justify-center" onClick={() => navigate('/portal/forgot-password')}>
          Request a new link <ArrowRight size={14} />
        </Button>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell tagline="Password reset" testId="reset-password-page">
        <h2 className="font-serif text-3xl font-semibold text-brand-navy">Password updated</h2>
        <p className="text-slate-500 mt-2">Sign in with your new password.</p>
        <Button variant="accent" className="mt-6 justify-center" onClick={() => navigate('/portal/login')}>
          Sign in <ArrowRight size={14} />
        </Button>
      </AuthShell>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (password.length < MIN_PASSWORD) {
      setFormError(`Password must be at least ${MIN_PASSWORD} characters.`);
      return;
    }
    if (password !== confirm) {
      setFormError("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post('/auth/password/reset', { token, password });
      setDone(true);
    } catch (e) {
      if (e.response?.status === 410 || e.response?.status === 404) {
        setError(messageOf(e));
      } else {
        setFormError(messageOf(e, 'Something went wrong. Try again.'));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell tagline="Password reset" testId="reset-password-page">
      <h2 className="font-serif text-3xl font-semibold text-brand-navy">Set a new password</h2>
      <p className="text-slate-500 mt-2">Choose a new password for your account.</p>
      <form onSubmit={submit} className="space-y-4 mt-8">
        <div>
          <Label>New password</Label>
          <PasswordInput
            value={password} onChange={(e) => setPassword(e.target.value)}
            required minLength={8} containerClassName="mt-1.5" placeholder="At least 8 characters" data-testid={RESET_PASSWORD.passwordInput}
          />
        </div>
        <div>
          <Label>Confirm password</Label>
          <PasswordInput
            value={confirm} onChange={(e) => setConfirm(e.target.value)}
            required minLength={8} containerClassName="mt-1.5" placeholder="Re-enter password" data-testid={RESET_PASSWORD.passwordConfirmInput}
          />
        </div>
        {formError && (
          <p className="text-sm text-red-600" role="alert" data-testid={RESET_PASSWORD.error}>{formError}</p>
        )}
        <Button type="submit" disabled={busy} variant="accent" className="w-full justify-center" data-testid={RESET_PASSWORD.submitButton}>
          {busy ? <><Loader2 size={16} className="animate-spin" /> Updating...</> : <>Update password <ArrowRight size={14} /></>}
        </Button>
      </form>
    </AuthShell>
  );
}
